// Global Instructions Rule Applied!
//
// Agent Drafts section — the drafts an agent wrote for this account or
// prospect, as cards inside a standard FormSection. Hidden-when-empty is the
// caller's choice (hideWhenEmpty) so a portfolio company with no drafts shows
// nothing.
//
// Two surfaces, one component, the same split AgentSignalsSection already
// uses (owner, 2026-09-04): the Dashboard shows a capped, hidden-when-empty
// copy because a draft is today's action item, and the Agent tab carries the
// full list with Show history. maxVisible + onViewMore are what make the
// Dashboard copy affordable — a draft card renders the whole email, so three
// of them would push the rest of the page out of sight.
//
// Collapsible since 2026-09-06 (owner), the same chevron the states strip and
// the signals section carry. Collapsed swaps each rendered email for a single
// line — status, subject, recipients, date — so folding reclaims the height a
// full draft takes without hiding that the draft is waiting. The states
// strip's pill row is the precedent, down to clicking a row to unfold.
import React, { useMemo, useState } from 'react'
import { Button, Switch, Tag } from 'antd'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { dismissErrorToast, showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { AgentDraftRecord } from '../../../state_manager/data_models/agent_drafts_model.ts'
import { AGENT_DRAFT_OPEN_STATUSES, useAgentDrafts } from '../../relationships/hooks/useAgentDrafts.ts'
import { useSectionCollapse } from '../../../core/hooks/useSectionCollapse.tsx'
import {
  confirmAgentDraftCandidate, exportAgentDraftToOutlook, fetchAgentDraftInteractionOptions, markAgentDraftSent, setAgentDraftStatus
} from '../services/agentDraftsService.ts'
import { HelpButton } from '../../../core/components/HelpDrawer/HelpButton.tsx'
import { HELP_ARTICLES } from '../../../ui_configs/helpArticles.ts'
import { AgentDraftCard, STATUS_COLOR, STATUS_LABEL } from './AgentDraftCard.tsx'

interface AgentDraftsSectionProps {
  contextTable: 'accounts' | 'prospects'
  contextId: string | null | undefined
  onRefreshReady?: (refreshFn: () => Promise<void>) => void
  hideWhenEmpty?: boolean
  className?: string
  title?: string
  /** Cap the cards rendered. Omitted = all of them (the Agent tab). */
  maxVisible?: number
  /** Shown as a "View more" link when the cap is hiding drafts; the Dashboard
   *  points this at the Agent tab's full list. */
  onViewMore?: () => void
  /** Surface key for the collapsed/expanded preference (e.g.
   *  'lp_dashboard_drafts'). Absent = always expanded, no chevron. */
  collapseKey?: string
  /** Starting position before the user has folded this surface. Drafts default
   *  OPEN — they are the action layer. */
  defaultCollapsed?: boolean
}

export function AgentDraftsSection({ contextTable, contextId, onRefreshReady, hideWhenEmpty = false, className, title = 'Agent Drafts', maxVisible, onViewMore, collapseKey, defaultCollapsed = false }: AgentDraftsSectionProps) {
  const { collapsed, setCollapsed, chevron } = useSectionCollapse(collapseKey, defaultCollapsed)
  const [showHistory, setShowHistory] = useState(false)
  const { drafts, loading, refetch, outlookCapability } = useAgentDrafts({ contextTable, contextId }, { includeHistory: showHistory })
  const [busyId, setBusyId] = useState<string | null>(null)

  // The cap never applies while Show history is on — the toggle exists to see
  // everything, so silently truncating it would make the switch look broken.
  const visible = useMemo(
    () => (maxVisible && !showHistory ? drafts.slice(0, maxVisible) : drafts),
    [drafts, maxVisible, showHistory]
  )
  const hiddenCount = drafts.length - visible.length
  // Counts OPEN work whether or not Show history is on, so the number beside
  // the title means the same thing in both positions of the switch.
  const openCount = useMemo(
    () => drafts.filter((d) => AGENT_DRAFT_OPEN_STATUSES.includes(d.agent_drafts__status)).length,
    [drafts]
  )

  React.useEffect(() => {
    if (onRefreshReady) onRefreshReady(refetch)
  }, [onRefreshReady, refetch])

  // A keyed error toast per draft: the failure stays up until the user closes
  // it or the same action later succeeds (FRONTEND_DESIGN_PATTERNS "Feedback
  // on a blocked action").
  const act = async (draft: AgentDraftRecord, fn: () => Promise<void>, done: string) => {
    const key = `agent-draft-${draft.agent_drafts__id}`
    setBusyId(draft.agent_drafts__id)
    try {
      await fn()
      dismissErrorToast(key)
      toast.success(done)
      await refetch()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Action failed', { key })
    } finally {
      setBusyId(null)
    }
  }

  if (hideWhenEmpty && !loading && drafts.length === 0 && !showHistory) return null

  // Collapsed: one line per draft — status, subject and recipients — rather
  // than an empty section (owner, 2026-09-06). Folding reclaims the height a
  // rendered email takes; it is not meant to hide that the email is waiting.
  // Clicking a row unfolds, as the states strip's pills do.
  const summaryRows = (
    <div className='flex flex-col'>
      {visible.map((d) => {
        const status = d.agent_drafts__status
        const to = (d.prog__suggested_to ?? []).map((c: any) => c.contacts__name).filter(Boolean).join(', ')
        return (
          <div
            key={d.agent_drafts__id}
            role='button'
            tabIndex={0}
            title={d.agent_drafts__rationale ?? d.agent_drafts__subject ?? ''}
            className='cursor-pointer flex items-baseline gap-2 py-1 text-[12px] min-w-0 hover:bg-gray-50 rounded'
            onClick={() => setCollapsed(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setCollapsed(false)
            }}
          >
            <Tag color={STATUS_COLOR[status] ?? 'default'} className='m-0 flex-none'>
              {STATUS_LABEL[status] ?? status}
            </Tag>
            <span className='truncate text-gray-700'>{d.agent_drafts__subject}</span>
            {to ? <span className='flex-none text-gray-500'>· to {to}</span> : null}
            <span className='ml-auto flex-none text-gray-400 text-[11px] tabular-nums'>
              {d.agent_drafts__date_created ? dayjs(d.agent_drafts__date_created).format('MM/DD/YY') : null}
            </span>
          </div>
        )
      })}
      {onViewMore && hiddenCount > 0 ? (
        <div className='flex justify-end pt-1'>
          <Button type='link' size='small' className='!px-0' onClick={onViewMore}>
            View more ({hiddenCount})
          </Button>
        </div>
      ) : null}
    </div>
  )

  return (
    <div data-component='AgentDraftsSection' className={className}>
      <FormSection
        title={title}
        titleColor='blue'
        loading={loading && drafts.length === 0}
        headerLeading={chevron}
        headerActions={
          <span className='inline-flex items-center gap-1 text-xs text-gray-600'>
            {openCount > 0 ? <Tag color='blue' className='m-0'>{openCount} open</Tag> : <Tag className='m-0'>none open</Tag>}
            {/* The switch has nothing to act on with the body folded away. */}
            {collapsed ? null : (
              <>
                <Switch size='small' checked={showHistory} onChange={setShowHistory} /> Show history
              </>
            )}
            <HelpButton articleKey={HELP_ARTICLES.agentDrafts} variant='section' />
          </span>
        }
      >
        {drafts.length === 0 ? (
          <div className='text-center text-gray-500 text-sm py-2'>No drafts</div>
        ) : collapsed ? (
          summaryRows
        ) : (
          <div className='flex flex-col gap-2'>
            {visible.map((d) => (
              <AgentDraftCard
                key={d.agent_drafts__id}
                draft={d}
                hideAccount
                busy={busyId === d.agent_drafts__id}
                outlookEnabled={outlookCapability.enabled}
                onAccept={(draft, action) =>
                  act(draft, () => setAgentDraftStatus(draft.agent_drafts__id, 'accepted', getAccessToken(store.getState().auth), { acceptedAction: action }), 'Draft copied')}
                onDismiss={(draft, reason) =>
                  act(draft, () => setAgentDraftStatus(draft.agent_drafts__id, 'dismissed', getAccessToken(store.getState().auth), { reason }), 'Draft dismissed')}
                onExportOutlook={(draft) =>
                  act(draft, async () => { await exportAgentDraftToOutlook(draft.agent_drafts__id, getAccessToken(store.getState().auth)) }, 'Draft created in your Outlook Drafts folder')}
                onConfirmCandidate={(draft, accept) =>
                  act(draft, async () => { await confirmAgentDraftCandidate(draft.agent_drafts__id, accept, getAccessToken(store.getState().auth)) }, accept ? 'Draft marked as sent' : 'Candidate dismissed')}
                onMarkSent={(draft, interactionId) =>
                  act(draft, async () => { await markAgentDraftSent(draft.agent_drafts__id, interactionId, getAccessToken(store.getState().auth)) }, 'Draft marked as sent')}
                loadInteractions={(draft) => fetchAgentDraftInteractionOptions(draft.agent_drafts__account_id, getAccessToken(store.getState().auth))}
              />
            ))}
            {onViewMore && hiddenCount > 0 ? (
              <div className='flex justify-end pt-1'>
                <Button type='link' size='small' className='!px-0' onClick={onViewMore}>
                  View more ({hiddenCount})
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </FormSection>
    </div>
  )
}

export default AgentDraftsSection
